import React, { useCallback, useMemo } from "react";

import { faChartSimple } from "@fortawesome/pro-solid-svg-icons/faChartSimple";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { Button, ButtonProps } from "@mapmycustomers/ui";

import { MAXIMUM_ENTITY_COUNT } from "./util/const";

interface Props extends Omit<ButtonProps, "onClick"> {
  disableTooltip?: boolean;
  entityCount: number;
  onClick?: () => void;
}

const DataViewButton: React.FC<Props> = ({ disableTooltip, entityCount, onClick, ...props }) => {
  const intl = useIntl();

  const handleClick = useCallback(() => onClick?.(), [onClick]);

  const disableText: string | undefined = useMemo(() => {
    if (disableTooltip) {
      return undefined;
    }
    if (entityCount > MAXIMUM_ENTITY_COUNT) {
      return intl.formatMessage(
        {
          id: "dataView.maximumEntityCountReached",
          defaultMessage:
            "Please select fewer than {count} {count, plural, one {record} other {records}} to use Data View",
          description: "Data view pane - Maximum reached",
        },
        { count: MAXIMUM_ENTITY_COUNT }
      );
    } else if (entityCount <= 0) {
      return intl.formatMessage({
        id: "dataView.minimumEntityCountReached",
        defaultMessage: "Please select at least one record to use Data View",
        description: "Data view pane - Data View - No entities",
      });
    }
    return undefined;
  }, [disableTooltip, entityCount, intl]);

  return (
    <Tooltip placement="bottom" title={disableText}>
      <Button
        {...props}
        disabled={!!disableText}
        leftIcon={props.loading ? undefined : <FontAwesomeIcon icon={faChartSimple} />}
        onClick={disableText ? undefined : handleClick}
        type="filled"
      >
        {intl.formatMessage({
          id: "dataView.button",
          defaultMessage: "Data View",
          description: "Data view pane - Data View",
        })}
      </Button>
    </Tooltip>
  );
};

export default DataViewButton;
